/**
 * Library Topic API Service
 * Handles all library topic management API calls
 */

import { apiGet, apiPost, apiPatch, apiDelete } from "./api";
import type {
  LibraryTopicModel,
  LibraryTopicListItem,
  CreateTopicRequest,
  UpdateTopicRequest,
  TopicStatsResponse,
  TopicUsageStats,
} from "@/types/models";

// ============================================================================
// Response Types from API (snake_case)
// ============================================================================

interface ApiLibraryTopic {
  id: string;
  slug: string;
  label: string;
  description?: string | null;
  display_order: number;
  is_active: boolean;
  resource_count?: number;
  created_at: string;
  updated_at: string;
}

interface ApiLibraryTopicListItem {
  id: string;
  slug: string;
  label: string;
  description?: string | null;
  display_order: number;
  is_active: boolean;
}

interface ApiTopicUsageStats {
  topic_id: string;
  slug: string;
  label: string;
  resource_count: number;
  active_resource_count: number;
}

interface ApiTopicStatsResponse {
  topics: ApiTopicUsageStats[];
  total_resources: number;
}

// ============================================================================
// Response Transformers (snake_case from API -> camelCase for frontend)
// ============================================================================

function transformTopic(topic: ApiLibraryTopic): LibraryTopicModel {
  return {
    id: topic.id,
    slug: topic.slug,
    label: topic.label,
    description: topic.description,
    displayOrder: topic.display_order,
    isActive: topic.is_active,
    resourceCount: topic.resource_count,
    createdAt: topic.created_at,
    updatedAt: topic.updated_at,
  };
}

function transformTopicListItem(
  topic: ApiLibraryTopicListItem,
): LibraryTopicListItem {
  return {
    id: topic.id,
    slug: topic.slug,
    label: topic.label,
    description: topic.description,
    displayOrder: topic.display_order,
    isActive: topic.is_active,
  };
}

function transformTopicStats(stats: ApiTopicUsageStats): TopicUsageStats {
  return {
    topicId: stats.topic_id,
    slug: stats.slug,
    label: stats.label,
    resourceCount: stats.resource_count,
    activeResourceCount: stats.active_resource_count,
  };
}

// ============================================================================
// Public Endpoints
// ============================================================================

/**
 * Get all active library topics
 * Available to all authenticated users
 */
export async function getLibraryTopics(): Promise<LibraryTopicListItem[]> {
  const topics = await apiGet<ApiLibraryTopicListItem[]>("/library/topics");
  return topics.map(transformTopicListItem);
}

// ============================================================================
// Admin Endpoints (requires super_admin role)
// ============================================================================

/**
 * Get all library topics including inactive ones
 * Admin only - includes resource counts
 */
export async function getAllLibraryTopics(): Promise<LibraryTopicModel[]> {
  const topics = await apiGet<ApiLibraryTopic[]>("/library/topics/all");
  return topics.map(transformTopic);
}

/**
 * Create a new library topic
 * Admin only
 */
export async function createLibraryTopic(
  data: CreateTopicRequest,
): Promise<LibraryTopicModel> {
  const topic = await apiPost<ApiLibraryTopic>("/library/topics", data);
  return transformTopic(topic);
}

/**
 * Update an existing library topic
 * Admin only
 */
export async function updateLibraryTopic(
  id: string,
  data: UpdateTopicRequest,
): Promise<LibraryTopicModel> {
  // Transform camelCase to snake_case for API
  const apiData: Record<string, unknown> = {};
  if (data.label !== undefined) apiData.label = data.label;
  if (data.description !== undefined) apiData.description = data.description;
  if (data.isActive !== undefined) apiData.is_active = data.isActive;
  if (data.displayOrder !== undefined)
    apiData.display_order = data.displayOrder;

  const topic = await apiPatch<ApiLibraryTopic>(
    `/library/topics/${id}`,
    apiData,
  );
  return transformTopic(topic);
}

/**
 * Delete a library topic
 * Admin only
 * @param force If true, hard delete (only works if no resources use this topic)
 */
export async function deleteLibraryTopic(
  id: string,
  force = false,
): Promise<void> {
  await apiDelete<void>(
    `/library/topics/${id}`,
    force ? { force: true } : undefined,
  );
}

/**
 * Reorder library topics
 * Admin only
 * @param topicIds Ordered list of topic IDs in the desired order
 */
export async function reorderLibraryTopics(
  topicIds: string[],
): Promise<LibraryTopicListItem[]> {
  const topics = await apiPost<ApiLibraryTopicListItem[]>(
    "/library/topics/reorder",
    { topic_ids: topicIds },
  );
  return topics.map(transformTopicListItem);
}

/**
 * Get topic usage statistics
 * Admin only
 */
export async function getTopicStats(): Promise<TopicStatsResponse> {
  const response = await apiGet<ApiTopicStatsResponse>("/library/topics/stats");
  return {
    topics: response.topics.map(transformTopicStats),
    totalResources: response.total_resources,
  };
}
